import crosswalkBundle from '../../data/ghgReportJurisdictionCrosswalk.json';

export interface CrosswalkEntry {
  capName: string;
  capCounty: string;
  stableJurisdictionId: string | null;
  censusGeoid: string | null;
  populationName: string | null;
  populationGeofips: number | null;
  matchMethod: string;
  aliasId: string | null;
  inPopulationCity: boolean;
  inFrozen2023Universe: boolean;
  incorporationDate?: string;
}

export interface UtilityLocationAlias {
  aliasSource: string;
  aliasName: string;
  capName: string;
  censusGeoid: string;
  populationGeofips: number;
  mustNotMatchCapName: string;
}

interface CrosswalkBundle {
  version: string;
  municipalityCount: number;
  frozen2023UniverseCount: number;
  entries: CrosswalkEntry[];
  utilityLocationAliases: UtilityLocationAlias[];
}

const BUNDLE = crosswalkBundle as CrosswalkBundle;
const ENTRIES = BUNDLE.entries;

const CAP_ALIAS_MAP: Record<string, CrosswalkEntry> = {
  'paso robles': ENTRIES.find((e) => e.capName === 'Paso Robles')!,
  ventura: ENTRIES.find((e) => e.capName === 'Ventura')!,
};

const UTILITY_ALIAS_MAP: Record<string, UtilityLocationAlias> = {};
for (const a of BUNDLE.utilityLocationAliases) {
  UTILITY_ALIAS_MAP[a.aliasName.trim().toUpperCase()] = a;
}

export function getCrosswalkEntries(): CrosswalkEntry[] {
  return ENTRIES;
}

export function getFrozen2023UniverseEntries(): CrosswalkEntry[] {
  return ENTRIES.filter((e) => e.inFrozen2023Universe);
}

export function getMunicipalityCount(): number {
  return BUNDLE.municipalityCount;
}

export function getFrozenUniverseCount(): number {
  return BUNDLE.frozen2023UniverseCount;
}

export function resolveByGeofips(geofips: number): CrosswalkEntry | null {
  return ENTRIES.find((e) => e.populationGeofips === geofips) ?? null;
}

export function resolveByCapName(capName: string): CrosswalkEntry | null {
  const key = capName.trim().toLowerCase();
  if (CAP_ALIAS_MAP[key]) return CAP_ALIAS_MAP[key];
  return ENTRIES.find((e) => e.capName.toLowerCase() === key) ?? null;
}

export function resolveUtilityLocationAlias(aliasName: string): CrosswalkEntry | null {
  const key = aliasName.trim().toUpperCase();
  const alias = UTILITY_ALIAS_MAP[key];
  if (!alias) return null;
  const entry = resolveByGeofips(alias.populationGeofips);
  if (!entry || entry.capName !== alias.capName) return null;
  if (entry.capName === alias.mustNotMatchCapName) return null;
  return entry;
}

export function resolveIdentifier(input: string | number): CrosswalkEntry | null {
  if (typeof input === 'number' || /^\d+$/.test(String(input))) {
    return resolveByGeofips(Number(input));
  }
  const utility = resolveUtilityLocationAlias(String(input));
  if (utility) return utility;
  return resolveByCapName(String(input));
}

export function isMountainHouse(entry: CrosswalkEntry): boolean {
  return entry.capName === 'Mountain House';
}

export function isInFrozen2023Universe(entry: CrosswalkEntry): boolean {
  return entry.inFrozen2023Universe;
}

export function toReportJurisdiction(entry: CrosswalkEntry) {
  return {
    jurisdictionId: entry.stableJurisdictionId ?? `cap:${entry.capName}`,
    stableJurisdictionId: entry.stableJurisdictionId,
    censusPlaceGeoid: entry.censusGeoid,
    populationGeofips: entry.populationGeofips,
    capName: entry.capName,
    canonicalName: entry.populationName ?? entry.capName,
    county: entry.capCounty,
    incorporationDate: entry.incorporationDate ?? null,
  };
}
